<script setup lang="ts">
import type {
  EventResponse,
  CommitmentResponse,
  RegistrationAttendeeResponse,
} from '@@/types/api';
import { useRegistrationApi } from '~/composables/api/use-registration-api';
import { useEventPricing } from '~/composables/api/use-event-pricing';

const props = defineProps<{
  modelValue: boolean;
  event: EventResponse;
  commitment: CommitmentResponse;
  attendees: RegistrationAttendeeResponse[];
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  updated: [];
}>();

const registrationApi = useRegistrationApi();

const isSubmitting = ref(false);
const errorMessage = ref<string | null>(null);
const newPartySize = ref(1);

// Resolve the registration type id used for pricing — prefer the commitment's
// own type, else fall back to the event's first type.
const pricingRegistrationTypeId = computed<string | null>(() => {
  const types = props.event.registration_types ?? [];
  if (props.commitment.registration_type_id) {
    const type = types.find(
      (t) => t.id === props.commitment.registration_type_id,
    );
    if (type) return type.id;
  }
  return types[0]?.id ?? null;
});

const pricing = useEventPricing(
  () => props.event.id,
  pricingRegistrationTypeId,
  newPartySize,
  'commit',
  { enabled: () => props.modelValue },
);

const claimedCount = computed(
  () => props.attendees.filter((a) => a.invite_status === 'claimed').length,
);

const maxPartySize = computed(() => {
  const types = props.event.registration_types ?? [];
  if (props.commitment.registration_type_id) {
    const type = types.find(
      (t) => t.id === props.commitment.registration_type_id,
    );
    if (type?.max_party_size != null) return type.max_party_size;
  }
  return types[0]?.max_party_size ?? null;
});

const partySizeRules = computed(() => {
  const max = maxPartySize.value;
  const min = claimedCount.value;
  return [
    (v: unknown) =>
      (Number.isInteger(Number(v)) && Number(v) >= 1) ||
      'Party size must be at least 1',
    (v: unknown) =>
      Number(v) >= min || `Cannot reduce below ${min} (already-claimed seats)`,
    (v: unknown) =>
      max == null || Number(v) <= max || `Maximum ${max} per registration`,
  ];
});

const partySizeValid = computed(() => {
  if (!Number.isInteger(newPartySize.value) || newPartySize.value < 1)
    return false;
  if (newPartySize.value < claimedCount.value) return false;
  if (maxPartySize.value != null && newPartySize.value > maxPartySize.value)
    return false;
  return true;
});

const canSubmit = computed(
  () =>
    !isSubmitting.value &&
    partySizeValid.value &&
    !pricing.isLoading.value &&
    !pricing.isStale.value &&
    pricing.breakdown.value !== null &&
    pricing.error.value === null,
);

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      // Initialize with the current attendee count
      newPartySize.value = props.attendees.length || 1;
      errorMessage.value = null;
    }
  },
  { immediate: true },
);

function close() {
  emit('update:modelValue', false);
}

async function handleSubmit() {
  if (!partySizeValid.value) return;
  if (newPartySize.value === props.attendees.length) {
    close();
    return;
  }
  isSubmitting.value = true;
  errorMessage.value = null;
  try {
    await registrationApi.updatePartySize(props.event.id, props.commitment.id, {
      party_size: newPartySize.value,
      version: props.commitment.version ?? 1,
    });
    emit('updated');
    close();
  } catch (e: unknown) {
    const err = e as { status?: number; message?: string };
    if (err.status === 409) {
      errorMessage.value =
        'This commitment was modified. Please refresh and try again.';
    } else if (err.status === 422) {
      errorMessage.value = err.message ?? 'Unable to update party size.';
    } else {
      errorMessage.value = 'Failed to update. Please try again.';
    }
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="480"
    :persistent="isSubmitting"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="text-h6">Update party size</v-card-title>
      <v-card-text>
        <p class="text-body-2 text-medium-emphasis mb-4">
          Change the number of attendees in your registration. Adding seats will
          increase your charge if the event reaches its goal.
        </p>

        <v-text-field
          v-model.number="newPartySize"
          label="Number of attendees"
          type="number"
          :min="Math.max(1, claimedCount)"
          :max="maxPartySize ?? undefined"
          :rules="partySizeRules"
          density="compact"
          class="mb-4"
        />

        <!-- Pricing breakdown -->
        <EventsPricingBreakdown
          :breakdown="pricing.breakdown.value"
          :party-size="newPartySize"
          :is-initial-loading="pricing.isInitialLoading.value"
          :is-refetching="pricing.isRefetching.value"
          :error-message="pricing.error.value?.message ?? null"
        />

        <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-2">
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="isSubmitting" @click="close">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="isSubmitting"
          :disabled="!canSubmit"
          @click="handleSubmit"
        >
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
